/**
 * AssignEquipeModal.tsx — Affectation d'un signalement à une ou plusieurs
 * personnes / une équipe (Bloc 2, fidèle au mockup validé).
 *
 * Ouverte au drop d'un signalement sur un jour du planning de travail :
 *   - équipes rapides (pré-cochent leurs membres présents ce jour) ;
 *   - grille de personnes cochables — SEULS les présents du jour sont proposés
 *     (les absents apparaissent grisés et non sélectionnables) ;
 *   - horaires début/fin ;
 *   - un seul bouton « Assigner à N personnes ».
 *
 * S'appuie sur POST /planning/assign-multi (pose un créneau par personne).
 */

import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import type { PlanningEquipe, PlanningPresenceJour, PlanningTemplate } from '@roronoa-collectivites/shared-types';

interface Props {
  /** signalement déposé (null si affectation d'un template ou libre) */
  signalement: { id: string; numero: string; sujet: string } | null;
  /** modèle de travaux déposé (null sinon) */
  template?: PlanningTemplate | null;
  /** date cible (jour du drop) */
  date: Date;
  /** heure de départ suggérée par le drop (ex. 14) */
  defaultHour: number;
  /** si true, affiche un sélecteur de date (usage hors glisser-déposer) */
  pickDate?: boolean;
  onClose: () => void;
  onDone: () => void;
}

function toLocalDateISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function toUtcISO(dateISO: string, hhmm: string): string {
  // Construit un instant depuis une date locale + heure locale (navigateur en
  // Europe/Paris côté mairie). new Date(y,m,d,h,mi) est en heure locale.
  const [y, mo, d] = dateISO.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  return new Date(y!, mo! - 1, d!, h!, mi!).toISOString();
}

export function AssignEquipeModal({ signalement, template, date, defaultHour, pickDate, onClose, onDone }: Props) {
  const [jourISO, setJourISO] = useState(toLocalDateISO(date));
  const dateISO = jourISO;
  const [equipes, setEquipes] = useState<PlanningEquipe[]>([]);
  const [presence, setPresence] = useState<PlanningPresenceJour | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [debut, setDebut] = useState(`${String(defaultHour).padStart(2, '0')}:00`);
  // Fin : depuis la durée du template si présent, sinon +2h par défaut.
  const finDefault = (() => {
    const dureeMin = template?.dureeMin ?? 120;
    const totalMin = defaultHour * 60 + dureeMin;
    const h = Math.min(23, Math.floor(totalMin / 60));
    const mi = totalMin % 60;
    return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
  })();
  const [fin, setFin] = useState(finDefault);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.serviceTechnique.planning.listEquipes().catch(() => [] as PlanningEquipe[]),
      api.serviceTechnique.planning.presenceJour(dateISO).catch(() => null),
    ]).then(([eq, pres]) => {
      if (!alive) return;
      setEquipes(eq);
      setPresence(pres);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [dateISO]);

  // Personnes présentes ce jour (les seules affectables)
  const presents = useMemo(() => presence?.agents ?? [], [presence]);
  const presentIds = useMemo(() => new Set(presents.map((p) => p.id)), [presents]);

  const initials = (name: string) => name.split(' ').map((w) => w[0]).slice(0, 2).join('');

  const toggle = (id: string) => {
    if (!presentIds.has(id)) return; // absent = non sélectionnable
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Applique une équipe rapide : coche ses membres PRÉSENTS ce jour
  const appliquerEquipe = (e: PlanningEquipe) => {
    const membresPresents = e.membres.filter((m) => presentIds.has(m.id)).map((m) => m.id);
    setSelected(new Set(membresPresents));
  };

  const assigner = async () => {
    if (selected.size === 0) { setError('Sélectionne au moins une personne présente.'); return; }
    if (fin <= debut) { setError('L\'heure de fin doit être après le début.'); return; }
    setBusy(true); setError(null);
    try {
      await api.serviceTechnique.planning.assignMulti({
        agentUserIds: Array.from(selected),
        dateDebut: toUtcISO(dateISO, debut),
        dateFin: toUtcISO(dateISO, fin),
        titre: signalement?.sujet ?? template?.titre ?? 'Intervention',
        signalementId: signalement?.id,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors de l\'affectation');
    } finally { setBusy(false); }
  };

  const jourLabel = new Date(`${jourISO}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Dialog open onClose={onClose} title={signalement ? `Affecter — ${signalement.numero}` : template ? `Affecter — ${template.titre}` : 'Affecter'} size="md">
      <p className="mb-3 text-xs text-ink-muted">
        {signalement?.sujet ?? template?.titre} · {jourLabel}
        {template && <span className="ml-1 text-tech-fg">· {template.nbPersonnes} personne{template.nbPersonnes > 1 ? 's' : ''} recommandée{template.nbPersonnes > 1 ? 's' : ''}</span>}
      </p>

      {loading ? (
        <p className="py-6 text-center text-sm text-ink-muted">Chargement…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Sélecteur de jour (hors glisser-déposer) */}
          {pickDate && (
            <div>
              <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Jour</label>
              <input type="date" value={jourISO} onChange={(e) => { setJourISO(e.target.value); setSelected(new Set()); }} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink" />
            </div>
          )}

          {/* Équipes rapides */}
          {equipes.length > 0 && (
            <div>
              <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Équipes rapides</label>
              <div className="flex flex-wrap gap-2">
                {equipes.map((e) => {
                  const nbPresents = e.membres.filter((m) => presentIds.has(m.id)).length;
                  return (
                    <button key={e.id} type="button" onClick={() => appliquerEquipe(e)}
                      className="flex items-center gap-1.5 rounded-r-pill border border-border-hi bg-bg-2/60 px-3 py-1.5 text-xs font-semibold text-ink-muted transition hover:border-tech hover:text-ink">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: e.couleur }} />
                      {e.nom} <span className="text-ink-dim">({nbPresents})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Personnes présentes */}
          <div>
            <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
              Qui intervient ? <span className="text-tech-fg">· {selected.size} sélectionné{selected.size > 1 ? 's' : ''}</span>
            </label>
            {presents.length === 0 ? (
              <p className="rounded-r-md border border-border-hi bg-bg-2/40 px-3 py-3 text-xs text-ink-muted">
                Personne n'est planifié présent ce jour-là. Ajoute d'abord de la présence (onglet Présence / Profils & génération).
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {presents.map((p) => {
                  const sel = selected.has(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => toggle(p.id)}
                      className={['flex items-center gap-2 rounded-r-md border px-2.5 py-2 text-left transition',
                        sel ? 'border-tech bg-tech-tint/50' : 'border-border-hi bg-bg-2/60'].join(' ')}>
                      <span className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: p.couleurMarker ?? '#A8A29E' }}>{initials(p.fullName)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-ink">{p.fullName.split(' ')[0]}</span>
                        <span className="block text-[9.5px] text-ink-subtle">{p.debut}–{p.fin}</span>
                      </span>
                      <span className={['grid h-4 w-4 place-items-center rounded border text-[10px] text-white', sel ? 'border-tech bg-tech' : 'border-border-hi'].join(' ')}>{sel ? '✓' : ''}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Horaires */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Début</label>
              <input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink" />
            </div>
            <div>
              <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Fin</label>
              <input type="time" value={fin} onChange={(e) => setFin(e.target.value)} className="w-full rounded-r-md border border-border-hi bg-bg-2/60 px-3 py-2 text-sm text-ink" />
            </div>
          </div>

          {error && <p className="text-xs text-urgent">{error}</p>}

          <div className="flex justify-end gap-2.5">
            <button type="button" onClick={onClose} className="rounded-r-md border border-border-hi bg-bg-2/60 px-4 py-2 text-sm font-semibold text-ink">Annuler</button>
            <button type="button" onClick={assigner} disabled={busy || selected.size === 0} className="rounded-r-md bg-tech px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? '…' : `Assigner à ${selected.size} personne${selected.size > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
